import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon, MailIcon, StarIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NetworkOrganizations } from '../components/NetworkOrganizations';
import { NearViewport } from '../components/shared/NearViewport';
import { PILLARS, PARTICIPATION, JOIN_STEPS, PRESIDENT_LETTER } from '../data/national-network-content';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/xhbs9gkKe39dSdAqRNeTtZ/NationalNetworkLogo.png';
const HAA_LOGO = 'https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const NAVY_DEEP = '#071427';
const BLUE = '#1b5fbe';
const SKY = '#3FA9F5';
const RED = '#e0343c';
const PALE = '#eef3fa';
const EASE = [0.16, 1, 0.3, 1] as const;
function SectionLabel({
  children,
  onDark = false
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{
    color: onDark ? SKY : BLUE
  }} data-id="element-3862" data-mp="2c7tysilrvbu0">
      <StarIcon className="w-3 h-3" style={{
      color: RED
    }} fill={RED} data-id="element-3863" data-mp="284e25m9pdbhb" />
      {children}
    </p>;
}
function MapLoading({
  isEs
}: {
  isEs: boolean;
}) {
  return <div className="w-full h-full flex items-center justify-center" style={{
    backgroundColor: NAVY_DEEP
  }} data-id="element-3864" data-mp="1y7j8phsae19w">
      <p className="font-nunito text-white/40 text-xs uppercase tracking-[0.2em]" data-id="element-3865" data-mp="1tbam5jbrf5sp">
        {isEs ? 'Cargando mapa…' : 'Loading map…'}
      </p>
    </div>;
}
export function NationalNetworkPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  const joinHref = mailto(isEs ? 'Solicitud de Membresía — Red Nacional Hondureño-Americana' : 'Membership Application — Honduran-American National Network');
  return <div className="min-h-screen w-full font-nunito antialiased" style={{
    backgroundColor: '#ffffff',
    color: NAVY
  }} data-id="element-3866" data-mp="1bzdrnslmyrkr">
      {/* Header — white bar carrying the National Network lockup */}
      <header className="sticky top-0 z-30 w-full bg-white shadow-sm" data-id="element-3867" data-mp="24p2ri2mm0lks">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4" data-id="element-3868" data-mp="1qos3r8gljn3u">
          <a href="#" className="flex items-center group" aria-label="Honduran American Association" data-id="element-3869" data-mp="1vnuwbtfe40ux">
            <img src={HAA_LOGO} alt="Honduran American Association" className="h-10 sm:h-12 w-auto object-contain group-hover:opacity-80 transition-opacity" decoding="async" data-id="element-3870" data-mp="f8tq0rah4cwa" />
          </a>
          <div className="flex items-center gap-4" data-id="element-3871" data-mp="7qdmzxx0l1ou">
            <div className="inline-flex rounded-full border overflow-hidden" style={{
            borderColor: 'rgba(11,31,63,0.25)'
          }} role="group" aria-label={isEs ? 'Idioma' : 'Language'} data-id="element-3872" data-mp="7pkht1dpek8t">
              {(['EN', 'ES'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} className="px-3.5 py-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: language === lang ? NAVY : 'transparent',
              color: language === lang ? '#ffffff' : 'rgba(11,31,63,0.6)'
            }} data-id="element-3873" data-mp="3p6v7sfqxsz5l">
                  {lang}
                </button>)}
            </div>
            <a href={joinHref} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
            backgroundColor: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = NAVY;
          }} data-id="element-3874" data-mp="24nt5fjpqwk0u">
              {isEs ? 'Únase' : 'Join'}
            </a>
            <a href="#" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-60" style={{
            color: NAVY
          }} data-id="element-3875" data-mp="2xet9tvrznpt1">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3876" data-mp="132mucks2amux" />
              {isEs ? 'Volver' : 'Back'}
            </a>
          </div>
        </div>
        <div className="h-[3px] w-full flex" data-id="element-3877" data-mp="zimjsatgdc8a">
          <div className="flex-1" style={{
          backgroundColor: BLUE
        }} data-id="element-3878" data-mp="zkfq1p05lufr" />
          <div className="flex-1 bg-white" data-id="element-3879" data-mp="uquppqb38ode" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3880" data-mp="psjebb55a5t2" />
        </div>
      </header>

      <main data-id="element-3881" data-mp="lqy6433k2fp7">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-3882" data-mp="lrzhy9seb67z">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.13]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)'
        }} data-id="element-3883" data-mp="1igoz1xw77usw" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20 text-center" data-id="element-3884" data-mp="3qpn3rekj88zm">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-3885" data-mp="3vlub9bsktsjo">
              <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.02] mb-8" data-id="element-3886" data-mp="3lnxr5uhnvn9f">
                <span className="block" style={{
                color: SKY
              }} data-id="element-3887" data-mp="37w9glvn296uk">
                  {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
                </span>
                <span className="block text-white" data-id="element-3888" data-mp="2xybt5ad0ze30">
                  {isEs ? 'Mayor Conexión' : 'Greater Connection'}
                </span>
                <span className="block" style={{
                color: RED
              }} data-id="element-3889" data-mp="2p0jca1rnnydi">
                  {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
                </span>
              </h1>
              <p className="font-nunito text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9" data-id="element-3890" data-mp="2ac9jseqqmabd">
                {isEs ? 'Un espacio nacional que conecta a las organizaciones, líderes y socios que ya sirven a las comunidades hondureñas en los Estados Unidos y en Honduras.' : 'A national space connecting the organizations, leaders, and partners already serving Honduran communities in the United States and in Honduras.'}
              </p>
              <a href={joinHref} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = NAVY;
            }} data-id="element-3891" data-mp="2ocka9go4xmw0">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3892" data-mp="nb15v1qlixac" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Logo band */}
        <section className="bg-white" data-id="element-3893" data-mp="3vq19ryzaep1c">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 lg:py-7 flex justify-center" data-id="element-3894" data-mp="3vmx1apesh74v">
            <motion.img initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} src={NETWORK_LOGO} alt="Honduran American Association National Network" className="w-[clamp(18rem,58vw,44rem)] h-auto object-contain" decoding="async" data-id="element-3895" data-mp="3lh1q4lged8n" />
          </div>
        </section>

        {/* 2 — Letter from the President */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3896" data-mp="34hre6pbaguom">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3897" data-mp="34i14yf45r5vd">
            <SectionLabel data-id="element-3898" data-mp="2zhdgo17bd34u">
              {isEs ? 'Carta del Presidente' : 'Letter from the President'}
            </SectionLabel>
            <p className="font-bebas text-3xl tracking-[0.04em] mb-7" style={{
            color: BLUE
          }} data-id="element-3899" data-mp="2vgjtxb5lxudj">
              {isEs ? PRESIDENT_LETTER.salutationEs : PRESIDENT_LETTER.salutation}
            </p>
            <div className="space-y-5 font-nunito text-base sm:text-[1.0625rem] leading-relaxed" style={{
            color: 'rgba(11,31,63,0.78)'
          }} data-id="element-3900" data-mp="2gsyr7icn9x65">
              {(isEs ? PRESIDENT_LETTER.paragraphsEs : PRESIDENT_LETTER.paragraphs).map(p => <p key={p.slice(0, 40)} data-id="element-3901" data-mp="22sxpy1sgwnq5">{p}</p>)}
            </div>
            <div className="mt-10 pt-7 border-t" style={{
            borderColor: 'rgba(11,31,63,0.15)'
          }} data-id="element-3902" data-mp="ho477tt7bg4t">
              <p className="font-nunito text-sm mb-7" style={{
              color: 'rgba(11,31,63,0.6)'
            }} data-id="element-3903" data-mp="coht7i5jtvuy">
                {isEs ? PRESIDENT_LETTER.closingEs : PRESIDENT_LETTER.closing}
              </p>
              <div className="grid sm:grid-cols-2 gap-8 sm:gap-6" data-id="element-3904" data-mp="3uv5lg7fi2oj7">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/mcPsQzGFDU6t8Dm21iMLQ5/154111606_32s61fjkqp-GREY.jpg',
                name: PRESIDENT_LETTER.name,
                role: isEs ? PRESIDENT_LETTER.roleEs : PRESIDENT_LETTER.role,
                position: '50% 22%',
                zoom: 1.28
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/kb8rqEBdvwMudH3CyTb87Y/ECallejas.jpg',
                name: 'Eva Callejas',
                role: isEs ? 'Vicepresidenta de Alianzas e Iniciativas Estratégicas' : 'Vice President of Partnerships & Strategic Initiatives',
                position: '50% 18%',
                zoom: 1
              }].map(person => <div key={person.name} className="flex items-center gap-5 sm:gap-6" data-id="element-3905" data-mp="25f3dhtonpshj">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden flex-shrink-0" style={{
                  border: `3px solid ${BLUE}`
                }} data-id="element-3906" data-mp="2ademk274nyyp">
                      <img src={person.src} alt={`${person.name}, ${person.role}`} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                    objectPosition: person.position,
                    transform: `scale(${person.zoom})`
                  }} data-id="element-3907" data-mp="20ma9j810io6i" />
                    </div>
                    <div data-id="element-3908" data-mp="2u1swbhgs2ls4">
                      <p className="font-bebas text-2xl tracking-[0.04em] leading-tight" style={{
                    color: NAVY
                  }} data-id="element-3909" data-mp="2p0lltan938hr">
                        {person.name}
                      </p>
                      <p className="font-nunito text-sm leading-snug" style={{
                    color: 'rgba(11,31,63,0.6)'
                  }} data-id="element-3910" data-mp="2fa0jbc4ghjtk">
                        {person.role}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* 1 — Member organizations, then the map */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3911" data-mp="1weafdjcyqdd1">
          {/* Member spotlights — expandable, logo-first */}
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 lg:pt-24" data-id="element-3912" data-mp="1r6u56zsf7wzt">
            <div className="mb-8" data-id="element-3913" data-mp="1r8x5qdr798is">
              <SectionLabel onDark data-id="element-3914" data-mp="1w5xa8k8ei37s">
                {isEs ? '01 — Miembros de la Red' : '01 — Network Members'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] text-white" data-id="element-3915" data-mp="214ibmmze2sxt">
                {isEs ? 'Conozca a las Organizaciones' : 'Meet the Organizations'}
              </h2>
            </div>
            <NetworkOrganizations isEs={isEs} data-id="element-3916" data-mp="1dffoizlzk7vx" />
          </div>

          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3917" data-mp="1i6fgdqg8zzrk">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3918" data-mp="1i8iekxrk0z7d">
              <div className="max-w-xl" data-id="element-3919" data-mp="kqrv2gr32c7a">
                <SectionLabel onDark data-id="element-3920" data-mp="ppv226qmishw">
                  {isEs ? '02 — Alcance' : '02 — Reach'}
                </SectionLabel>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] text-white" data-id="element-3921" data-mp="2rgc0mvyl308q">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-white/50 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3922" data-mp="316ohoe76wba4">
                {isEs ? 'Seleccione un punto para ver la organización y su trabajo en Honduras' : 'Select a point to see the organization and its work in Honduras'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/12" style={{
            backgroundColor: NAVY_DEEP
          }} data-id="element-3923" data-mp="3k3y2nzs5xwth">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10" data-id="element-3924" data-mp="3f5efxx5qwh02">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3925" data-mp="1pqq4by4zb333" />
                <span className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3926" data-mp="8t5zqk3hmpy">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              {/* The map library and its tiles load only as the visitor
                  scrolls toward this section, keeping first paint light. */}
              <NearViewport className="h-[440px] sm:h-[560px] w-full" placeholder={<MapLoading isEs={isEs} data-id="element-3928" data-mp="3dmef71mwaoc9" />} data-id="element-3927" data-mp="3rlw5jlfua9nq">
                <Suspense fallback={<MapLoading isEs={isEs} data-id="element-3930" data-mp="3ink9h3w7ejnw" />} data-id="element-3929" data-mp="3in2gqh8r2nkq">
                  <NetworkMap isEs={isEs} data-id="element-3931" data-mp="3ikg07s3vsxqd" />
                </Suspense>
              </NearViewport>
            </div>
          </div>
        </section>

        {/* 3 — Pillars of Collaboration */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3932" data-mp="38qfxfww2o4kt">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3933" data-mp="11zggh1q0imhj">
            <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-3934" data-mp="16xi0ppz6crul">
              <SectionLabel onDark data-id="element-3935" data-mp="15g2skej2w820">
                {isEs ? '03 — La Red' : '03 — The Network'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: '#ffffff'
            }} data-id="element-3936" data-mp="10ggds0nlqjr3">
                {isEs ? 'Pilares de Colaboración' : 'Pillars of Collaboration'}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-id="element-3937" data-mp="1o6apgew6q29r">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <motion.article key={pillar.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="relative pl-14" data-id="element-3938" data-mp="1egirrbssgt12" data-mpi={i}>
                    <span className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center font-bebas text-base tracking-wide text-white" style={{
                  backgroundColor: i % 2 === 0 ? SKY : 'rgba(255,255,255,0.14)'
                }} aria-hidden="true" data-id="element-3939" data-mp="3prwolh6bv97p">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.6} data-id="element-3940" data-mp="cjjui5db72lu" />
                    <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-3 text-white" data-id="element-3941" data-mp="ldgfvhyngb07">
                      {isEs ? pillar.titleEs : pillar.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(255,255,255,0.68)'
                }} data-id="element-3942" data-mp="v9t9ygy3wqvh">
                      {isEs ? pillar.textEs : pillar.text}
                    </p>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* 3 — Year One participation */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3943" data-mp="3jndd9zvsx0l4">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3944" data-mp="3jo4ycgrrvfx5">
            <div className="max-w-2xl mb-14" data-id="element-3945" data-mp="3eqd77oyybxu0">
              <SectionLabel data-id="element-3946" data-mp="3sfyiseaf9as0">
                {isEs ? '04 — Participación' : '04 — Participation'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3947" data-mp="3np8hbc549s2l">
                {isEs ? 'Cómo Se Ve la Participación en el Primer Año' : 'What Participation Can Look Like in Year One'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.7)'
            }} data-id="element-3948" data-mp="2lsasho3m6fbj">
                {isEs ? 'Construyendo conexiones, oportunidades e impacto compartido. Las organizaciones participantes podrán involucrarse de maneras que se alineen con su misión, capacidad y prioridades.' : 'Building connections, opportunities, and shared impact together. Participating organizations will have opportunities to engage in ways that align with their mission, capacity, and priorities.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3949" data-mp="2zlimfmf63mwe">
              {PARTICIPATION.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="rounded-xl border bg-white p-7 h-full flex flex-col" style={{
                borderColor: 'rgba(11,31,63,0.12)'
              }} data-id="element-3950" data-mp="1o6lt40byabj8" data-mpi={i}>
                    <Icon className="w-5 h-5 mb-4" style={{
                  color: BLUE
                }} strokeWidth={1.7} data-id="element-3951" data-mp="59kxcxl71s1j" />
                    <h3 className="font-bebas text-xl tracking-[0.06em] uppercase mb-3" style={{
                  color: NAVY
                }} data-id="element-3952" data-mp="3se0ox1y4d940">
                      {isEs ? item.titleEs : item.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3953" data-mp="3nfz68i4lvbt2">
                      {isEs ? item.textEs : item.text}
                    </p>
                  </motion.div>;
            })}
            </div>

            <p className="font-nunito text-sm sm:text-base leading-relaxed max-w-3xl mt-10" style={{
            color: 'rgba(11,31,63,0.68)'
          }} data-id="element-3954" data-mp="3dups69gg175l">
              {isEs ? 'La Red está diseñada para ser flexible. Las organizaciones pueden participar en las oportunidades más relevantes para su trabajo y capacidad. Nuestra meta es facilitar que las organizaciones se conecten, colaboren, compartan recursos y persigan oportunidades juntas.' : 'The Network is designed to be flexible. Organizations may participate in the opportunities most relevant to their work and capacity. Our goal is to make it easier for organizations to connect, collaborate, share resources, and pursue opportunities together.'}
            </p>
          </div>
        </section>

        {/* 5 — How do I join */}
        <section className="bg-white" data-id="element-3955" data-mp="hfcwczlrei25">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3956" data-mp="hge4owbe4qg6">
            <div className="text-center mb-14" data-id="element-3957" data-mp="mdxwkz10h1ql">
              <SectionLabel data-id="element-3958" data-mp="md4l5kr3xo24">
                {isEs ? '05 — Membresía' : '05 — Membership'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: NAVY
            }} data-id="element-3959" data-mp="v7ja6f1fwcyf">
                {isEs ? '¿Cómo Me Uno?' : 'How Do I Join?'}
              </h2>
            </div>

            <ol className="space-y-10" data-id="element-3960" data-mp="1568fl8c5g4xs">
              {JOIN_STEPS.map((step, i) => <motion.li key={step.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="relative pl-16 sm:pl-20 pb-10 border-b last:border-b-0 last:pb-0" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3961" data-mp="1a41pabcnvh2t" data-mpi={i}>
                  <span className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center font-bebas text-lg tracking-wide text-white" style={{
                backgroundColor: i === 1 ? NAVY : BLUE
              }} aria-hidden="true" data-id="element-3962" data-mp="1fagkifd2gqt1">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-bebas text-3xl tracking-[0.04em] mb-4" style={{
                color: BLUE
              }} data-id="element-3963" data-mp="1y0ohsgu34n1b">
                    {isEs ? step.titleEs : step.title}
                  </h3>
                  <p className="font-nunito text-base leading-relaxed" style={{
                color: 'rgba(11,31,63,0.78)'
              }} data-id="element-3964" data-mp="mjezhxmp011l">
                    {isEs ? step.leadEs : step.lead}
                  </p>
                  {(isEs ? step.pointsEs : step.points).length > 0 ? <ul className="mt-5 space-y-3" data-id="element-3965" data-mp="21ewrvsxcafye">
                      {(isEs ? step.pointsEs : step.points).map(point => <li key={point.slice(0, 40)} className="flex gap-3" data-id="element-3966" data-mp="2u4vny89ybdck">
                          <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0" style={{
                    color: RED
                  }} strokeWidth={2.5} data-id="element-3967" data-mp="2y70cisljljsc" />
                          <span className="font-nunito text-sm leading-relaxed" style={{
                    color: 'rgba(11,31,63,0.68)'
                  }} data-id="element-3968" data-mp="2p5j1vpqwmab1">
                            {point}
                          </span>
                        </li>)}
                    </ul> : null}
                  {i === 0 ? <a href={joinHref} className="inline-flex items-center gap-2 mt-5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                color: BLUE
              }} data-id="element-3969" data-mp="117hy5e1oyua1">
                      <MailIcon className="w-3.5 h-3.5" data-id="element-3970" data-mp="mkosl0hz52v1" />
                      {isEs ? 'Solicitar información' : 'Request information'}
                    </a> : null}
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* 5 — CTA */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3971" data-mp="rhox36z6dxk1">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3972" data-mp="rgx483wyqdxg">
            <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[1.02] mb-7" data-id="element-3973" data-mp="3r2onel9eb9i">
              <span className="block" style={{
              color: SKY
            }} data-id="element-3974" data-mp="3uxdtllte3w64">
                {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
              </span>
              <span className="block text-white" data-id="element-3975" data-mp="23u3wykt6epmf">
                {isEs ? 'Mayor Conexión' : 'Greater Connection'}
              </span>
              <span className="block" style={{
              color: RED
            }} data-id="element-3976" data-mp="28ohkk11ljsgj">
                {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
              </span>
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-9" data-id="element-3977" data-mp="1fz0uvb5d2po0">
              {isEs ? 'Queremos construir esto con ustedes, no simplemente para ustedes. Si su organización sirve a las comunidades hondureñas o hondureño-americanas, nos encantaría conocerla.' : 'We want to build this with you, not simply for you. If your organization serves Honduran or Honduran-American communities, we would be honored to know you.'}
            </p>
            <a href={joinHref} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3978" data-mp="35at8jlua5ewj">
              <MailIcon className="w-4 h-4" data-id="element-3979" data-mp="5yom503xjx5s" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white py-9 border-t" style={{
      borderColor: 'rgba(11,31,63,0.12)'
    }} data-id="element-3980" data-mp="1zplvst58oko">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3981" data-mp="3t6ip127hyxru">
          <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-12 w-auto object-contain" loading="lazy" decoding="async" data-id="element-3982" data-mp="1rd2kr2ihlkee" />
          <p className="font-nunito text-[10px] uppercase tracking-[0.22em]" style={{
          color: 'rgba(11,31,63,0.5)'
        }} data-id="element-3983" data-mp="155iao3x42nqt">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}